import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COMPANY_SIZES = ['Under 50', '51–100', '101–250', '251–500', '501–1,000', '1,000+'];
const INDUSTRIES = ['SaaS', 'eCommerce', 'Auction & marketplace', 'Other'];

const emptyForm = { name: '', company: '', company_size: '', industry: '', feedback: '' };

const Feedback = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Required validation
    for (const [key, val] of Object.entries(formData)) {
      if (!val || !String(val).trim()) {
        toast.error('Please complete all fields', { description: 'All fields are required.' });
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/feedback`, formData);
      if (data.success) {
        setSubmitted(true);
        setFormData(emptyForm);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error('Error', { description: typeof detail === 'string' ? detail : 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls = 'w-full bg-white border border-gray-300 text-gray-900 px-6 py-4 text-[18px] focus:outline-none focus:border-[#00D4B4] transition-colors';

  return (
    <div className="min-h-screen py-20 md:py-32 bg-white" data-testid="feedback-page">
      <div className="container mx-auto px-[7.6923%]">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-[36px] sm:text-[48px] lg:text-[66px] font-semibold leading-[1.1] tracking-[-0.62px] text-gray-900 mb-6">
            Client Feedback
          </h1>
          <p className="text-[20px] text-gray-900/85 max-w-3xl mx-auto">
            Worked with CX Sage? Share your experience below. Every submission is reviewed privately — nothing is published without approval.
          </p>
        </div>

        {/* Submission Form / Thank you */}
        <div className="max-w-2xl mx-auto bg-gray-50 p-6 sm:p-12 border border-gray-200">
          {submitted ? (
            <div className="text-center py-8" data-testid="feedback-thankyou">
              <div className="w-20 h-20 bg-[#00D4B4] flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-[24px] md:text-[32px] font-semibold text-gray-900 mb-4">Thank You!</h2>
              <p className="text-[18px] text-gray-900/85 leading-[1.6] mb-8">
                Your feedback has been received and will be reviewed shortly. I truly appreciate you taking the time to share it.
              </p>
              <button onClick={() => setSubmitted(false)} data-testid="feedback-another-button"
                className="inline-flex bg-[#00D4B4] text-white px-8 py-4 text-[18px] font-medium hover:bg-[#00BFA3] transition-colors" style={{ borderRadius: '0px' }}>
                Submit Another
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-[24px] md:text-[32px] font-semibold text-gray-900 mb-8">Share Your Experience</h2>
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div>
                  <label htmlFor="name" className="block text-[18px] font-medium text-gray-900 mb-3">Full Name *</label>
                  <input id="name" name="name" required value={formData.name} onChange={handleChange}
                    className={inputCls} style={{ borderRadius: '0px' }} placeholder="Jane Doe" data-testid="feedback-name-input" />
                </div>
                <div>
                  <label htmlFor="company" className="block text-[18px] font-medium text-gray-900 mb-3">Company Name *</label>
                  <input id="company" name="company" required value={formData.company} onChange={handleChange}
                    className={inputCls} style={{ borderRadius: '0px' }} placeholder="Your Company" data-testid="feedback-company-input" />
                </div>
                <div>
                  <label htmlFor="company_size" className="block text-[18px] font-medium text-gray-900 mb-3">Company Size *</label>
                  <select id="company_size" name="company_size" required value={formData.company_size} onChange={handleChange}
                    className={inputCls} style={{ borderRadius: '0px' }} data-testid="feedback-company-size-select">
                    <option value="">Select company size</option>
                    {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="industry" className="block text-[18px] font-medium text-gray-900 mb-3">Industry *</label>
                  <select id="industry" name="industry" required value={formData.industry} onChange={handleChange}
                    className={inputCls} style={{ borderRadius: '0px' }} data-testid="feedback-industry-select">
                    <option value="">Select industry</option>
                    {INDUSTRIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="feedback" className="block text-[18px] font-medium text-gray-900 mb-3">Feedback *</label>
                  <textarea id="feedback" name="feedback" required rows={6} value={formData.feedback} onChange={handleChange}
                    className={`${inputCls} resize-none`} style={{ borderRadius: '0px' }}
                    placeholder="Tell us about your experience working with CX Sage..." data-testid="feedback-message-input" />
                </div>
                <button type="submit" disabled={isSubmitting} data-testid="feedback-submit-button"
                  className="w-full bg-[#00D4B4] text-black px-8 py-5 text-lg font-medium hover:bg-[#00D4B4]/10 hover:text-[#00D4B4] transition-all duration-400 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderRadius: '0px' }}>
                  {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Feedback;
